import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiPlusBold, PiPencilSimpleBold, PiTrashBold,
    PiWarningBold, PiBuildingBold, PiPhoneBold, PiUserBold,
    PiCalendarCheckBold, PiCurrencyDollarSimpleBold,
    PiPackageBold, PiCheckBold,
} from 'react-icons/pi';
import { PageHeader, Skeleton } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type DC = {
    id: number;
    name: string;
    slug: string;
    code: string | null;
    address_line: string | null;
    district: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    responsible: string | null;
    notes: string | null;
    free_storage_days: number;
    daily_rate_brl: number;
    active: boolean;
    sort_order: number;
    locations_count: number;
};

type ApiList = {
    data: DC[];
    meta: { current_page: number; last_page: number; total: number; from: number | null; to: number | null };
};

function formatBrl(v: number) {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });
}

function DeleteConfirm({ target, onClose }: { target: DC; onClose: () => void }) {
    const qc = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: () => api.delete(`/distribution-centers/${target.id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['distribution-centers'] });
            onClose();
        },
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
            <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10">
                        <PiWarningBold className="size-5 text-red-500" />
                    </div>
                    <div>
                        <h3 className="font-black text-gray-900 dark:text-white">Excluir CD</h3>
                        <p className="text-xs text-gray-400 dark:text-zinc-600">{target.name}</p>
                    </div>
                </div>
                <p className="mb-5 text-sm text-gray-500 dark:text-zinc-400">
                    Esta ação excluirá o CD e <strong>{target.locations_count}</strong> localização(ões) vinculada(s). Não pode ser desfeita.
                </p>
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => mutate()}
                        disabled={isPending}
                        className="rounded-xl bg-red-500 px-4 py-2 text-sm font-black text-white hover:bg-red-600 disabled:opacity-60"
                    >
                        {isPending ? 'Excluindo…' : 'Excluir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DcCard({ dc, onDelete }: { dc: DC; onDelete: () => void }) {
    const address = [dc.address_line, dc.district, dc.city, dc.state].filter(Boolean).join(', ');

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-5 dark:border-zinc-800">
                <div className="flex items-center gap-3">
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm">
                        <PiBuildingBold className="size-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-black text-gray-900 dark:text-white">{dc.name}</h3>
                            {dc.code && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                                    {dc.code}
                                </span>
                            )}
                        </div>
                        {address && <p className="mt-0.5 text-xs text-gray-400 dark:text-zinc-600">{address}</p>}
                    </div>
                </div>
                <span className={cn(
                    'shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-black',
                    dc.active
                        ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400'
                        : 'border border-gray-200 bg-gray-100 text-gray-400 dark:border-zinc-700 dark:bg-zinc-800',
                )}>
                    {dc.active ? 'Ativo' : 'Inativo'}
                </span>
            </div>

            <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-zinc-800">
                <div className="flex flex-col items-center gap-1 py-4">
                    <PiPackageBold className="size-4 text-gray-400 dark:text-zinc-600" />
                    <p className="text-lg font-black text-gray-900 dark:text-white">{dc.locations_count}</p>
                    <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Localizações</p>
                </div>
                <div className="flex flex-col items-center gap-1 py-4">
                    <PiCalendarCheckBold className="size-4 text-amber-500 dark:text-amber-400" />
                    <p className="text-lg font-black text-gray-900 dark:text-white">{dc.free_storage_days}</p>
                    <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">Dias grátis</p>
                </div>
                <div className="flex flex-col items-center gap-1 py-4">
                    <PiCurrencyDollarSimpleBold className="size-4 text-emerald-500 dark:text-emerald-400" />
                    <p className="text-lg font-black text-gray-900 dark:text-white">{formatBrl(dc.daily_rate_brl ?? 0)}</p>
                    <p className="text-[10px] font-bold uppercase text-gray-400 dark:text-zinc-600">/ m³·dia</p>
                </div>
            </div>

            {(dc.responsible || dc.phone) && (
                <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 px-5 py-3 dark:border-zinc-800">
                    {dc.responsible && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500">
                            <PiUserBold className="size-3.5 shrink-0 text-gray-400" />
                            {dc.responsible}
                        </div>
                    )}
                    {dc.phone && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-zinc-500">
                            <PiPhoneBold className="size-3.5 shrink-0 text-gray-400" />
                            {dc.phone}
                        </div>
                    )}
                </div>
            )}

            <div className="flex border-t border-gray-100 dark:border-zinc-800">
                <Link
                    to={`/settings/distribution-centers/${dc.id}/edit`}
                    className="flex flex-1 items-center justify-center gap-2 py-3 text-xs font-black text-gray-400 transition hover:bg-amber-50 hover:text-amber-600 dark:text-zinc-600 dark:hover:bg-amber-500/10 dark:hover:text-amber-400"
                >
                    <PiPencilSimpleBold className="size-3.5" /> Editar
                </Link>
                <div className="w-px bg-gray-100 dark:bg-zinc-800" />
                <button
                    type="button"
                    onClick={onDelete}
                    className="flex flex-1 items-center justify-center gap-2 py-3 text-xs font-black text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:text-zinc-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                >
                    <PiTrashBold className="size-3.5" /> Excluir
                </button>
            </div>
        </div>
    );
}

export default function DistributionCentersPage() {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState<DC | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['distribution-centers'],
        queryFn: async () => {
            const { data: body } = await api.get<ApiList>('/distribution-centers', { params: { per_page: '100' } });
            return body.data;
        },
        staleTime: 60_000,
    });

    const dcs = data ?? [];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Centros de Distribuição"
                breadcrumb={[{ name: 'Configurações' }, { name: 'Centros de Distribuição' }]}
            >
                <button
                    type="button"
                    onClick={() => navigate('/settings/distribution-centers/new')}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white transition hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900"
                >
                    <PiPlusBold className="size-4" /> Novo CD
                </button>
            </PageHeader>

            {dcs.length > 0 && (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    {[
                        { label: 'CDs cadastrados', value: dcs.length, icon: <PiBuildingBold className="size-5" />, color: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400' },
                        { label: 'Localizações totais', value: dcs.reduce((s, d) => s + (d.locations_count ?? 0), 0), icon: <PiPackageBold className="size-5" />, color: 'bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400' },
                        { label: 'Dias grátis (média)', value: Math.round(dcs.reduce((s, d) => s + d.free_storage_days, 0) / dcs.length) + ' dias', icon: <PiCalendarCheckBold className="size-5" />, color: 'bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400' },
                        { label: 'CDs ativos', value: dcs.filter((d) => d.active).length, icon: <PiCheckBold className="size-5" />, color: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400' },
                    ].map((s) => (
                        <div key={s.label} className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                            <div className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl', s.color)}>{s.icon}</div>
                            <div>
                                <p className="text-xl font-black text-gray-900 dark:text-white">{s.value}</p>
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{s.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isLoading ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="space-y-3 rounded-2xl border border-gray-200 p-5 dark:border-zinc-800">
                            <div className="flex items-center gap-3">
                                <Skeleton className="size-12 rounded-2xl" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-3 w-48" />
                                </div>
                            </div>
                            <Skeleton className="h-20 w-full rounded-xl" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                    ))}
                </div>
            ) : dcs.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 py-16 text-gray-400 dark:border-zinc-800 dark:text-zinc-600">
                    <PiBuildingBold className="mb-2 size-10 opacity-30" />
                    <p className="font-bold">Nenhum centro de distribuição cadastrado</p>
                    <button
                        type="button"
                        onClick={() => navigate('/settings/distribution-centers/new')}
                        className="mt-4 flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900"
                    >
                        <PiPlusBold className="size-4" /> Cadastrar CD
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {dcs.map((dc) => (
                        <DcCard
                            key={dc.id}
                            dc={dc}
                            onDelete={() => setDeleting(dc)}
                        />
                    ))}
                </div>
            )}

            {deleting && <DeleteConfirm target={deleting} onClose={() => setDeleting(null)} />}
        </div>
    );
}
